import { useMoneyFormatter } from "@/lib/money-format";
import { usePaymentMethods } from "@/lib/api/payment-methods";
import { useTranslation } from "react-i18next";
import { PaymentMethodsCard } from "@/components/treasury/PaymentMethodsCard";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle2,
  Landmark,
  Loader2,
  Plus,
  Search,
  WalletCards,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBankAccounts,
  useBankReconciliations,
  useBankTransactions,
  useCreateBankAccount,
  useCreateBankReconciliation,
  useCreateBankTransaction,
} from "@/lib/api/banking.queries";
import type { BankAccount, ReconciliationStatus } from "@/lib/api/banking";
import { useActiveCompany } from "@/lib/company-context";
import { fmtDate } from "@/components/inventory/format";
import { MoneyInput, PucAccountLookup } from "@/components/shared";

export const Route = createFileRoute("/companies/$companyId/bancos")({
  component: BancosPage,
});

const STATUS_LABEL: Record<ReconciliationStatus, string> = {
  unreconciled: "Sin conciliar",
  cleared: "Conciliado",
  ignored: "Ignorado",
};

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function BancosPage() {
  return (
    <RequireAuth>
      <AppShell>
        <BancosContent />
      </AppShell>
    </RequireAuth>
  );
}

function BancosContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const accountsQuery = useBankAccounts(companyId);
  const accounts = useMemo(
    () => accountsQuery.data?.accounts ?? [],
    [accountsQuery.data?.accounts],
  );
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  useEffect(() => {
    if (!selectedAccountId && accounts[0]) setSelectedAccountId(accounts[0].id);
  }, [accounts, selectedAccountId]);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a la empresa
          </Link>
          <div className="flex items-center gap-2">
            <Landmark className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Bancos y conciliacion
            </h1>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Cuentas de caja y bancos, movimientos del extracto y conciliacion
            contra el saldo final del periodo.
          </p>
        </div>
      </header>

      <PaymentMethodsCard companyId={companyId} />
      {accountsQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : accountsQuery.isError ? (
        <ErrorState
          title="No pudimos cargar las cuentas bancarias"
          description={accountsQuery.error?.message ?? ""}
          onRetry={() => void accountsQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <BankAccountsPanel
            companyId={companyId}
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            onSelect={setSelectedAccountId}
          />
          {selectedAccount ? (
            <BankAccountWorkspace
              companyId={companyId}
              account={selectedAccount}
            />
          ) : (
            <Card>
              <CardContent className="flex min-h-72 flex-col items-center justify-center gap-3 text-center">
                <WalletCards className="h-10 w-10 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Crea tu primera cuenta</p>
                  <p className="mt-1 max-w-md text-sm text-muted-foreground">
                    Usa una cuenta auxiliar del PUC para caja, bancos o tarjeta
                    de credito y empieza a registrar movimientos conciliables.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function BankAccountsPanel(props: {
  companyId: string;
  accounts: BankAccount[];
  selectedAccountId: string;
  onSelect: (accountId: string) => void;
}) {
  const fmtCop = useMoneyFormatter();
  const createAccount = useCreateBankAccount(props.companyId);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [accountCode, setAccountCode] = useState("111005");
  const [bankName, setBankName] = useState("");
  const [openingBalance, setOpeningBalance] = useState<number | null>(0);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    createAccount.mutate(
      {
        name: name.trim(),
        accountCode: accountCode.trim(),
        bankName: bankName.trim() || undefined,
        accountType: bankName.trim() ? "checking" : "cash",
        openingBalance: openingBalance ?? 0,
      },
      {
        onSuccess: (account) => {
          toast.success("Cuenta bancaria creada");
          props.onSelect(account.id);
          setShowForm(false);
          setName("");
          setBankName("");
          setOpeningBalance(0);
        },
        onError: (err) =>
          toast.error("No pudimos crear la cuenta", {
            description: err.message,
          }),
      },
    );
  }

  return (
    <Card className="h-fit">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Cuentas</CardTitle>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            Nueva
          </Button>
        </div>
        <CardDescription>
          Caja, bancos y tarjetas amarradas a una auxiliar PUC.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm ? (
          <form className="grid gap-3 rounded-md border p-3" onSubmit={submit}>
            <div className="grid gap-1.5">
              <Label htmlFor="bank-name">Nombre</Label>
              <Input
                id="bank-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Bancolombia corriente"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="bank-account-code">Cuenta PUC</Label>
              <PucAccountLookup
                id="bank-account-code"
                companyId={props.companyId}
                value={accountCode}
                onChange={(code) => setAccountCode(code)}
                preferPrefixes={["1105", "1110", "1120"]}
                compact
                placeholder="1105 / 1110"
              />
              <p className="text-[11px] text-muted-foreground">
                Familia 1105 (caja) / 1110 (bancos): elige la auxiliar donde se
                contabilizan los movimientos.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="bank-opening">Saldo inicial</Label>
              <MoneyInput
                id="bank-opening"
                value={openingBalance}
                onValueChange={setOpeningBalance}
                allowNegative
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="bank-bank-name">Banco</Label>
              <Input
                id="bank-bank-name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Opcional"
              />
            </div>
            <Button
              type="submit"
              disabled={createAccount.isPending || !name.trim()}
            >
              {createAccount.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Crear cuenta
            </Button>
          </form>
        ) : null}

        <div className="space-y-2">
          {props.accounts.map((account) => {
            const selected = account.id === props.selectedAccountId;
            return (
              <button
                key={account.id}
                type="button"
                onClick={() => props.onSelect(account.id)}
                className={[
                  "w-full rounded-md border px-3 py-2 text-left transition-colors",
                  selected ? "border-primary bg-primary/10" : "hover:bg-accent",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{account.name}</span>
                  <Badge
                    variant={account.unreconciledCount ? "warning" : "muted"}
                  >
                    {account.unreconciledCount}
                  </Badge>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>{account.accountCode}</span>
                  <span>{fmtCop(account.currentBalance)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function BankAccountWorkspace(props: {
  companyId: string;
  account: BankAccount;
}) {
  const fmtCop = useMoneyFormatter();
  const { t } = useTranslation();
  const methods = usePaymentMethods(props.companyId);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [status, setStatus] = useState<ReconciliationStatus | "">(
    "unreconciled",
  );
  const [q, setQ] = useState("");
  const [statementStart, setStatementStart] = useState(
    todayIsoDate().slice(0, 8) + "01",
  );
  const [statementEnd, setStatementEnd] = useState(todayIsoDate());
  const [statementEndingBalance, setStatementEndingBalance] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    props.account.id,
    status,
    q,
    statementStart,
    statementEnd,
    paymentMethodId,
  ]);

  const transactionsQuery = useBankTransactions(
    props.companyId,
    props.account.id,
    {
      paymentMethodId: paymentMethodId || undefined,
      status: status || undefined,
      q: q || undefined,
      dateFrom: statementStart || undefined,
      dateTo: statementEnd || undefined,
      pageSize: 200,
    },
  );
  const reconciliationsQuery = useBankReconciliations(
    props.companyId,
    props.account.id,
    { pageSize: 5 },
  );
  const transactions = useMemo(
    () => transactionsQuery.data?.transactions ?? [],
    [transactionsQuery.data?.transactions],
  );
  const selectedTransactions = useMemo(
    () => transactions.filter((tx) => selectedIds.has(tx.id)),
    [transactions, selectedIds],
  );
  const selectedAmount = selectedTransactions.reduce(
    (sum, tx) => sum + tx.amount,
    0,
  );
  const targetEnding =
    statementEndingBalance.trim() === ""
      ? null
      : Number(statementEndingBalance);
  const projectedEnding = props.account.clearedBalance + selectedAmount;
  const projectedDifference =
    targetEnding === null ? null : projectedEnding - targetEnding;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground">Saldo actual</p>
            <p className="mt-1 text-xl font-semibold">
              {fmtCop(props.account.currentBalance)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground">Sin conciliar</p>
            <p className="mt-1 text-xl font-semibold">
              {props.account.unreconciledCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground">Cuenta PUC</p>
            <p className="mt-1 text-sm font-medium">
              {props.account.accountCode}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {props.account.accountName ?? "Cuenta personalizada"}
            </p>
          </CardContent>
        </Card>
      </div>

      <BankTransactionForm
        companyId={props.companyId}
        accountId={props.account.id}
      />

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Movimientos bancarios</CardTitle>
              <CardDescription>
                Selecciona movimientos sin conciliar para cerrar el extracto.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                aria-label={t("paymentMethods.title")}
                value={paymentMethodId}
                onChange={(e) => setPaymentMethodId(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                <option value="">{t("paymentMethods.all")}</option>
                {methods.data?.paymentMethods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.name}
                  </option>
                ))}
              </select>
              <select
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as ReconciliationStatus | "")
                }
              >
                <option value="">Todos</option>
                <option value="unreconciled">Sin conciliar</option>
                <option value="cleared">Conciliados</option>
                <option value="ignored">Ignorados</option>
              </select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="w-56 pl-8"
                  placeholder="Buscar descripcion"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto]">
            <Input
              type="date"
              value={statementStart}
              onChange={(e) => setStatementStart(e.target.value)}
            />
            <Input
              type="date"
              value={statementEnd}
              onChange={(e) => setStatementEnd(e.target.value)}
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Saldo final extracto"
              value={statementEndingBalance}
              onChange={(e) => setStatementEndingBalance(e.target.value)}
            />
            <ReconcileButton
              companyId={props.companyId}
              accountId={props.account.id}
              statementStart={statementStart}
              statementEnd={statementEnd}
              statementEndingBalance={targetEnding}
              transactionIds={Array.from(selectedIds)}
            />
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>Seleccionado: {fmtCop(selectedAmount)}</span>
            <span>Saldo proyectado: {fmtCop(projectedEnding)}</span>
            <span>
              Diferencia:{" "}
              {projectedDifference === null ? "—" : fmtCop(projectedDifference)}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {transactionsQuery.isPending ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : transactionsQuery.isError ? (
            <ErrorState
              title="No pudimos cargar los movimientos"
              description={transactionsQuery.error?.message ?? ""}
              onRetry={() => void transactionsQuery.refetch()}
              retryLabel="Reintentar"
            />
          ) : transactions.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No hay movimientos para este filtro.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => {
                    const canSelect =
                      tx.reconciliationStatus === "unreconciled";
                    const checked = selectedIds.has(tx.id);
                    return (
                      <TableRow key={tx.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            disabled={!canSelect}
                            checked={checked}
                            onChange={(e) => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(tx.id);
                                else next.delete(tx.id);
                                return next;
                              });
                            }}
                            aria-label={`Seleccionar ${tx.description}`}
                          />
                        </TableCell>
                        <TableCell>{fmtDate(tx.transactionDate)}</TableCell>
                        <TableCell className="min-w-56">
                          {tx.description}
                          {tx.paymentMethodName ? (
                            <p className="text-xs text-muted-foreground">
                              {tx.paymentMethodName}
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell>{tx.reference ?? "—"}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              tx.reconciliationStatus === "cleared"
                                ? "success"
                                : tx.reconciliationStatus === "ignored"
                                  ? "muted"
                                  : "warning"
                            }
                          >
                            {STATUS_LABEL[tx.reconciliationStatus]}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={
                            tx.amount >= 0
                              ? "text-right text-emerald-700"
                              : "text-right text-destructive"
                          }
                        >
                          {fmtCop(tx.amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conciliaciones recientes</CardTitle>
          <CardDescription>
            Extractos cerrados que ya cuadraron.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(reconciliationsQuery.data?.reconciliations ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aun no hay conciliaciones posteadas.
            </p>
          ) : (
            <div className="space-y-2">
              {(reconciliationsQuery.data?.reconciliations ?? []).map((rec) => (
                <div
                  key={rec.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2"
                >
                  <div className="text-sm">
                    <span className="font-medium">
                      {fmtDate(rec.statementStart)} –{" "}
                      {fmtDate(rec.statementEnd)}
                    </span>
                    <span className="ml-2 text-muted-foreground">
                      {rec.lineCount} movimiento{rec.lineCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    {fmtCop(rec.statementEndingBalance)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BankTransactionForm(props: { companyId: string; accountId: string }) {
  const createTransaction = useCreateBankTransaction(
    props.companyId,
    props.accountId,
  );
  const [transactionDate, setTransactionDate] = useState(todayIsoDate());
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState<number | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    createTransaction.mutate(
      {
        transactionDate,
        description: description.trim(),
        reference: reference.trim() || undefined,
        amount: amount ?? 0,
      },
      {
        onSuccess: () => {
          toast.success("Movimiento registrado");
          setDescription("");
          setReference("");
          setAmount(null);
        },
        onError: (err) =>
          toast.error("No pudimos registrar el movimiento", {
            description: err.message,
          }),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Nuevo movimiento</CardTitle>
        <CardDescription>
          Valores positivos son entradas; negativos son salidas.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={submit}
          className="grid gap-3 lg:grid-cols-[150px_1.5fr_1fr_150px_auto]"
        >
          <Input
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
          />
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripcion"
            required
          />
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Referencia"
          />
          <MoneyInput
            value={amount}
            onValueChange={setAmount}
            placeholder="Valor"
            allowNegative
            aria-label="Valor"
          />
          <Button
            type="submit"
            disabled={
              createTransaction.isPending ||
              !description.trim() ||
              (amount ?? 0) === 0
            }
          >
            {createTransaction.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Agregar
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ReconcileButton(props: {
  companyId: string;
  accountId: string;
  statementStart: string;
  statementEnd: string;
  statementEndingBalance: number | null;
  transactionIds: string[];
}) {
  const createReconciliation = useCreateBankReconciliation(
    props.companyId,
    props.accountId,
  );
  const disabled =
    createReconciliation.isPending ||
    !props.statementStart ||
    !props.statementEnd ||
    props.statementEndingBalance === null ||
    props.transactionIds.length === 0;

  return (
    <Button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (props.statementEndingBalance === null) return;
        createReconciliation.mutate(
          {
            statementStart: props.statementStart,
            statementEnd: props.statementEnd,
            statementEndingBalance: props.statementEndingBalance,
            transactionIds: props.transactionIds,
          },
          {
            onSuccess: (result) =>
              toast.success("Conciliacion posteada", {
                description: `${result.clearedTransactionCount} movimiento(s) conciliado(s).`,
              }),
            onError: (err) =>
              toast.error("La conciliacion no cuadra", {
                description: err.message,
              }),
          },
        );
      }}
    >
      {createReconciliation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <CheckCircle2 className="h-4 w-4" />
      )}
      Conciliar
    </Button>
  );
}
